<template>
  <div
    class="settings"
    :class="{ active: showSettingsMenu }"
  >
    <NorticIconButton
      ref="toggleButton"
      :icon="require('@icons/streamline-icons/Outline version/01-Interface Essential/12-Settings/cog.svg')"
      theme-type="accent"
      slim
      :selected="showSettingsMenu"
      @click="openMenu"
    />
    <NorticList
      v-if="showSettingsMenu"
      ref="settingsList"
      :list-style="listStyles"
      portal-target="portalTarget"
      position="bottom-start"
      :relative-to="$refs.toggleButton"
      :items="visibleSettingsModules"
    >
      <template #list-container="{ items }">
        <ul v-on-clickaway:mousedown="closeMenu">
          <template v-for="(item) in items">
            <RouterLink
              :key="item.key"
              #default="{ isActive, navigate }"
              :to="getRouterPath(item.key)"
            >
              <NorticListItem
                :label="item.moduleName"
                :item-data="item"
                :is-active="isActive"
                :item-container-style="menuItemStyles"
                @click.native="navigate"
              >
                <template #default="{ label }">
                  {{ label }}
                </template>
              </NorticListItem>
            </RouterLink>
          </template>
          <!-- Custom items here: -->
          <NorticListItem
            ref="themeItem"
            label="Teman"
            class="theme-item"
            :class="{ 'is-open': isThemesMenuVisible }"
            :children="visibleThemes"
            :is-active="isThemesMenuVisible"
            :item-container-style="menuItemStyles"
            :child-item-label-mapper="(child) => child.label"
            :child-item-selected-mapper="(child) => child.value === selectedTheme.name"
            :child-item-key-mapper="(child) => child.value"
            @click="toggleThemesMenu"
            @child-click="selectTheme"
          >
            <template #default="{ label, isParent, level, isActive }">
              <div
                class="theme-item-container"
                :class="{ 'is-parent': isParent, 'is-child': level > 1, 'is-active': isActive }"
              >
                <span>
                  {{ label }}
                </span>
                <span
                  v-if="isParent"
                  class="sub-menu-icon"
                  :class="{ 'is-open': isThemesMenuVisible }"
                >
                  <SvgIcon :data="require('@icons/streamline-icons/Outline version/52-Arrows-Diagrams/01-Arrows/arrow-down-1.svg')" />
                </span>
              </div>
            </template>
          </NorticListItem>
        </ul>
      </template>
    </NorticList>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2';
import { mapGetters } from 'vuex';
import startCase from 'lodash/startCase';
import GetterTypes from '@/store/constants/getters';
import routerUtils from '@/components/common/mixins/routerUtils';
import MutationTypes from '@/store/constants/mutations';
import themes from '@/themes/themes';
import NorticIconButton from '@/components/common/button/nortic-button/NorticIconButton.vue';
import NorticList from '@/components/common/data/nortic-list/NorticList.vue';
import NorticListItem from '@/components/common/data/nortic-list/NorticListItem.vue';

export default {
  name: 'SettingsButtonV2',
  components: {
    NorticListItem,
    NorticList,
    NorticIconButton,
  },
  mixins: [clickaway, routerUtils],
  data() {
    return {
      showSettingsMenu: false,
      isThemesMenuVisible: false,
      listStyles: { width: 'max-content' },
      menuItemStyles: { padding: '0.8em 1.5em', fontWeight: '600' },
      themeItems: Object.keys(themes)
        .map((theme) => ({
          label: startCase(theme),
          value: themes[theme].name,
        })),
    };
  },
  computed: {
    ...mapGetters([GetterTypes.SETTINGS_MODULE]),
    visibleSettingsModules() {
      return this.settingsModule
        ?.subAuthorityModules
        .filter((module) => module.authority === 'granted' || module.authority === 'teaser');
    },
    visibleThemes() {
      return this.isThemesMenuVisible
        ? this.themeItems
        : [];
    },
    theme() {
      return this.$store.getters[GetterTypes.THEME];
    },
  },
  methods: {
    openMenu() {
      this.showSettingsMenu = true;
    },
    closeMenu(e) {
      if (!this.$refs?.themesList?.$refs?.norticList?.contains(e.target)) {
        this.showSettingsMenu = false;
        this.isThemesMenuVisible = false;
      }
    },
    selectTheme(item) {
      this.emitThemeChange(item.value);
    },
    emitThemeChange(value) {
      this.$store.commit(MutationTypes.SET_THEME, value);
    },
    toggleThemesMenu() {
      this.isThemesMenuVisible = !this.isThemesMenuVisible;
    },
  },
};
</script>

<style lang="scss" scoped>
  .settings::v-deep {
    &.active {
      button {
        svg {
          transform: rotate(360deg);
        }
      }
    }

    user-select: none;
    position: relative;
    align-self: stretch;
    display: grid;
    align-items: center;
    justify-items: center;
    border-left: none;
    border-bottom: 1px solid var(--primary-divider-hex);

    button {
      svg {
        transition: transform 0.7s ease-in-out;
      }
    }
  }

  hr {
    margin: 0;
    border-color: var(--border-color-hex);
  }

  ul {
    display: grid;
    margin: 0;
    padding: 0;
  }

  .theme-item {
    .theme-item-container {
      &.is-child {
        &.is-active {
          color: var(--input-active-border-hex);
        }
      }

      user-select: none;
      display: grid;
      grid-template-columns: 1fr auto;
      align-content: center;
      align-items: center;

      .sub-menu-icon {
        &.is-open {
          svg {
            transform: rotate(-180deg);
          }
        }

        svg {
          transition: transform 0.3s;
        }
      }
    }
  }
</style>
